import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { agenteSupervisionAPI } from '../../services/api';
import { Loader2, Activity, AlertTriangle, Clock, CheckCircle2, XCircle, LifeBuoy, Cpu, Download, RefreshCw, TrendingUp } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import PanelTendencias from './PanelTendencias';

// P13G — Panel de supervisión de sesiones de los tres agentes (sólo lectura, determinista).
const ESTADO_ESTILO = {
  activa: 'bg-blue-100 text-blue-800',
  estancada: 'bg-amber-100 text-amber-800',
  completada: 'bg-emerald-100 text-emerald-800',
  abandonada: 'bg-gray-100 text-gray-600',
  escalada: 'bg-purple-100 text-purple-800',
  con_error: 'bg-red-100 text-red-800',
};
const ESTADO_LABEL = {
  activa: 'Activa', estancada: 'Estancada', completada: 'Completada',
  abandonada: 'Abandonada', escalada: 'Escalada a humano', con_error: 'Con error',
};
const AGENTES = [
  { id: '', label: 'Todos los agentes' },
  { id: 'gestor_inventario', label: 'Inventario (A1)' },
  { id: 'atencion_clientes', label: 'Atención (A2)' },
  { id: 'prospectos_afiliacion', label: 'Afiliación (A3)' },
];

const Metrica = ({ etiqueta, valor, testid, tono = 'text-gray-900' }) => (
  <div className="border rounded-xl p-3 bg-white" data-testid={testid}>
    <p className="text-xs text-gray-500">{etiqueta}</p>
    <p className={`text-xl font-semibold ${tono}`}>{valor}</p>
  </div>
);

export const PanelSupervisionAgentes = ({ seccion }) => {
  const { toast } = useToast();
  const [resumen, setResumen] = useState(null);
  const [sesiones, setSesiones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [agente, setAgente] = useState('');
  const [estado, setEstado] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [detalle, setDetalle] = useState(null);
  const [autoSeg, setAutoSeg] = useState(0);
  const [rev, setRev] = useState({ revisada: true, asignar: false, nota: '' });
  const [guardando, setGuardando] = useState(false);
  const [soloSinResolver, setSoloSinResolver] = useState(false);
  const [vista, setVista] = useState('sesiones');
  const [errRes, setErrRes] = useState({});

  const filtros = () => ({ section_id: seccion || undefined, agente: agente || undefined,
    estado: estado || undefined, desde: desde || undefined, hasta: hasta || undefined });

  const cargar = async () => {
    setCargando(true);
    try {
      const [r, s] = await Promise.all([
        agenteSupervisionAPI.resumen(filtros()),
        agenteSupervisionAPI.sesiones({ ...filtros(), limite: 200,
          solo_sin_resolver: soloSinResolver || undefined }),
      ]);
      setResumen(r);
      setSesiones(s.items || []);
    } catch (e) { /* silencioso: panel de sólo lectura */ } finally { setCargando(false); }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ },
    [seccion, agente, estado, desde, hasta, soloSinResolver]);

  // Auto-refresco: apagado por defecto; intervalos acotados y seguros (sólo lectura).
  useEffect(() => {
    if (!autoSeg) return undefined;
    const id = setInterval(cargar, autoSeg * 1000);
    return () => clearInterval(id);
    /* eslint-disable-next-line */
  }, [autoSeg, seccion, agente, estado, desde, hasta]);

  const exportarCsv = async () => {
    try {
      await agenteSupervisionAPI.exportarCsv(filtros());
      toast({ title: 'CSV exportado', description: 'Sin datos personales de clientes.' });
    } catch (e) { toast({ title: 'No se pudo exportar', variant: 'destructive' }); }
  };

  const guardarRevision = async () => {
    if (!detalle) return;
    setGuardando(true);
    try {
      await agenteSupervisionAPI.revision(detalle.sesion_id, {
        revisada: rev.revisada, nota: rev.nota,
        responsable_id: rev.asignar ? detalle.asesor?.id : undefined,
      });
      toast({ title: rev.revisada ? 'Sesión marcada como revisada' : 'Marcada como pendiente' });
      const actualizado = await agenteSupervisionAPI.detalle(detalle.sesion_id);
      setDetalle(actualizado);
      cargar();
    } catch (e) { toast({ title: 'No se pudo guardar la revisión', variant: 'destructive' }); }
    finally { setGuardando(false); }
  };

  const resolverError = async (errorId, resuelto) => {
    const f = errRes[errorId] || {};
    try {
      await agenteSupervisionAPI.resolverError(errorId, {
        resuelto, motivo: f.motivo || (resuelto ? 'Resuelto desde el panel' : 'Reabierto'),
        nota: f.nota, responsable_id: f.asignar ? detalle.asesor?.id : undefined });
      toast({ title: resuelto ? 'Error marcado como resuelto' : 'Error reabierto' });
      const actualizado = await agenteSupervisionAPI.detalle(detalle.sesion_id);
      setDetalle(actualizado);
      cargar();
    } catch (e) { toast({ title: 'No se pudo actualizar el error', variant: 'destructive' }); }
  };

  const t = resumen?.totales || {};
  const alertas = resumen?.alertas || {};
  const fh = (v) => (v ? new Date(v).toLocaleString('es-MX') : '—');

  return (
    <div className="space-y-4" data-testid="panel-supervision">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col">
          <label className="text-xs text-gray-500 mb-1">Agente</label>
          <select value={agente} onChange={(e) => setAgente(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm bg-white"
                  data-testid="supervision-filtro-agente">
            {AGENTES.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-gray-500 mb-1">Estado</label>
          <select value={estado} onChange={(e) => setEstado(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm bg-white"
                  data-testid="supervision-filtro-estado">
            <option value="">Todos</option>
            {(resumen?.estados_posibles || Object.keys(ESTADO_LABEL)).map((s) => (
              <option key={s} value={s}>{ESTADO_LABEL[s] || s}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-gray-500 mb-1">Desde</label>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
                 className="border rounded-lg px-3 py-2 text-sm bg-white"
                 data-testid="supervision-filtro-desde" />
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-gray-500 mb-1">Hasta</label>
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
                 className="border rounded-lg px-3 py-2 text-sm bg-white"
                 data-testid="supervision-filtro-hasta" />
        </div>
        <div className="ml-auto flex items-end gap-2">
          <div className="flex gap-1 self-center" role="group" aria-label="Vista">
            <Button size="sm" variant={vista === 'sesiones' ? 'default' : 'outline'}
                    onClick={() => setVista('sesiones')} data-testid="supervision-vista-sesiones">
              Sesiones
            </Button>
            <Button size="sm" variant={vista === 'tendencias' ? 'default' : 'outline'}
                    onClick={() => setVista('tendencias')} data-testid="supervision-vista-tendencias">
              <TrendingUp className="h-4 w-4 mr-1" />Tendencias
            </Button>
          </div>
          <label className="flex items-center gap-1 text-xs text-gray-600 self-center">
            <input type="checkbox" checked={soloSinResolver}
                   onChange={(e) => setSoloSinResolver(e.target.checked)}
                   data-testid="supervision-solo-sin-resolver" />
            Sólo errores sin resolver
          </label>
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Auto-refresco</label>
            <select value={autoSeg} onChange={(e) => setAutoSeg(Number(e.target.value))}
                    className="border rounded-lg px-3 py-2 text-sm bg-white"
                    data-testid="supervision-auto-refresco">
              <option value={0}>Apagado</option>
              <option value={15}>15 s</option>
              <option value={30}>30 s</option>
              <option value={60}>60 s</option>
            </select>
          </div>
          <Button variant="outline" onClick={exportarCsv} data-testid="supervision-exportar-csv">
            <Download className="h-4 w-4 mr-2" /> CSV
          </Button>
          {resumen?.ia_estado && (
            <Badge className="bg-slate-100 text-slate-600 self-center"
                   data-testid="supervision-ia-estado">
              <Cpu className="h-3 w-3 mr-1" />
              {resumen.ia_estado.activa ? 'asistencia IA activa'
                : 'IA no configurada · determinista'}
            </Badge>
          )}
          {autoSeg > 0 && (
            <RefreshCw className="h-4 w-4 text-emerald-600 animate-spin self-center"
                       data-testid="supervision-auto-activo" />
          )}
        </div>
      </div>

      {vista === 'tendencias' && <PanelTendencias seccion={seccion} agente={agente} />}
      {vista === 'sesiones' && (
      <>

      {cargando && (
        <div className="flex items-center text-gray-500 py-6" data-testid="supervision-cargando">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Cargando supervisión…
        </div>
      )}

      {!cargando && resumen && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <Metrica etiqueta="Total" valor={t.total ?? 0} testid="supervision-total" />
            <Metrica etiqueta="Activas" valor={t.activa ?? 0} tono="text-blue-700"
                     testid="supervision-activas" />
            <Metrica etiqueta="Completadas" valor={t.completada ?? 0} tono="text-emerald-700"
                     testid="supervision-completadas" />
            <Metrica etiqueta="Abandonadas" valor={t.abandonada ?? 0} testid="supervision-abandonadas" />
            <Metrica etiqueta="Escaladas" valor={t.escalada ?? 0} tono="text-purple-700"
                     testid="supervision-escaladas" />
            <Metrica etiqueta="Con error" valor={t.con_error ?? 0} tono="text-red-700"
                     testid="supervision-con-error" />
            <Metrica etiqueta="Tasa finalización" valor={`${t.tasa_finalizacion ?? 0}%`}
                     testid="supervision-tasa" />
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            {(resumen.por_agente || []).map((a) => (
              <Card key={a.agente} data-testid={`supervision-agente-${a.agente}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Activity className="h-4 w-4 text-indigo-600" /> {a.titulo}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-gray-600 space-y-1">
                  <p>Total: <b>{a.total}</b> · Finalización: <b>{a.tasa_finalizacion}%</b></p>
                  <p>Activas {a.activa} · Completadas {a.completada} · Abandonadas {a.abandonada}</p>
                  <p>Escaladas {a.escalada} · Con error <span className={a.con_error > 0
                    ? 'text-red-600 font-medium' : ''}>{a.con_error}</span> ·
                    {' '}Estancadas {a.estancada}</p>
                  <p>Tiempo medio: <b>{a.tiempo_medio_min != null ? `${a.tiempo_medio_min} min`
                    : '—'}</b></p>
                </CardContent>
              </Card>
            ))}
          </div>

          {(alertas.sesiones_estancadas?.length > 0 || alertas.sesiones_con_error?.length > 0
            || alertas.escaladas_pendientes > 0) && (
            <Card className="border-amber-200 bg-amber-50/60" data-testid="supervision-alertas">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2 text-amber-800">
                  <AlertTriangle className="h-4 w-4" /> Alertas internas (sin envío externo)
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-gray-700 space-y-1">
                <p><Clock className="h-3 w-3 inline mr-1" />
                  Estancadas (&gt;{alertas.umbral_estancada_min} min):
                  {' '}<b>{alertas.sesiones_estancadas?.length ?? 0}</b></p>
                <p><XCircle className="h-3 w-3 inline mr-1 text-red-600" />
                  Con error: <b>{alertas.sesiones_con_error?.length ?? 0}</b>
                  {(alertas.errores_repetidos_por_agente || []).length > 0 &&
                    ` · errores repetidos en ${alertas.errores_repetidos_por_agente
                      .map((x) => x.titulo).join(', ')}`}</p>
                <p><LifeBuoy className="h-3 w-3 inline mr-1 text-purple-600" />
                  Escaladas pendientes: <b>{alertas.escaladas_pendientes ?? 0}</b></p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Sesiones · {sesiones.length} registro(s)</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="supervision-tabla">
                <thead className="text-left text-gray-500 border-b">
                  <tr>{['Agente', 'Estado', 'Sección', 'Asesor', 'Progreso', 'Interac.',
                    'Inactiva', 'Creada', ''].map((c) => (
                    <th key={c} className="py-2 pr-4 font-medium">{c}</th>))}</tr>
                </thead>
                <tbody>
                  {sesiones.map((s) => (
                    <tr key={s.sesion_id} className="border-b last:border-0"
                        data-testid={`supervision-fila-${s.sesion_id}`}>
                      <td className="py-2 pr-4">{s.agente_titulo}</td>
                      <td className="py-2 pr-4">
                        <Badge className={ESTADO_ESTILO[s.estado_supervision]
                          || 'bg-gray-100 text-gray-600'}>
                          {ESTADO_LABEL[s.estado_supervision] || s.estado_supervision}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4">{s.seccion}</td>
                      <td className="py-2 pr-4">{s.asesor?.nombre || '—'}</td>
                      <td className="py-2 pr-4">{s.progreso.respondidos}/{s.progreso.total_pasos}</td>
                      <td className="py-2 pr-4">{s.interacciones}</td>
                      <td className="py-2 pr-4">{s.minutos_inactiva != null
                        ? `${s.minutos_inactiva} min` : '—'}</td>
                      <td className="py-2 pr-4">{fh(s.creada_en)}</td>
                      <td className="py-2 pr-4">
                        <Button size="sm" variant="outline"
                                onClick={() => agenteSupervisionAPI.detalle(s.sesion_id)
                                  .then((d) => { setDetalle(d);
                                    setRev({ revisada: !d.revision?.revisada, asignar: false,
                                             nota: d.revision?.nota || '' }); })}
                                data-testid={`supervision-detalle-${s.sesion_id}`}>
                          Ver{s.revision?.revisada ? ' ✓' : ''}
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {sesiones.length === 0 && (
                    <tr><td colSpan={9} className="py-6 text-center text-gray-400">
                      Sin sesiones para los filtros seleccionados
                    </td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
      </>
      )}


      {detalle && (
        <Dialog open onOpenChange={() => setDetalle(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"
                         data-testid="supervision-detalle-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {detalle.agente_titulo}
                <Badge className={ESTADO_ESTILO[detalle.estado_supervision]}>
                  {ESTADO_LABEL[detalle.estado_supervision]}
                </Badge>
              </DialogTitle>
            </DialogHeader>
            <div className="text-sm text-gray-700 space-y-2">
              <p>Sección: <b>{detalle.seccion}</b> · Asesor: <b>{detalle.asesor?.nombre || '—'}</b></p>
              <p>Canal: {detalle.canal} · Interacciones: {detalle.interacciones} ·
                {' '}Consentimiento: {detalle.consentimiento ? 'sí' : 'no'}</p>
              <p>Creada: {fh(detalle.creada_en)} · Cerrada: {fh(detalle.cerrada_en)}
                {detalle.duracion_seg != null && ` · Duración: ${Math.round(
                  detalle.duracion_seg / 60)} min`}</p>
              {detalle.motivo_handoff && (
                <p className="text-purple-700">Motivo de escalación: {detalle.motivo_handoff}</p>
              )}
              {detalle.motivo_cancelacion && (
                <p className="text-gray-600">Motivo de cancelación: {detalle.motivo_cancelacion}</p>
              )}
              <div className="border rounded-xl p-3 bg-gray-50" data-testid="supervision-pasos">
                <p className="font-medium text-gray-700 mb-2 flex items-center gap-1">
                  <CheckCircle2 className="h-4 w-4" /> Trazabilidad de pasos (PII redactada)
                </p>
                <ol className="space-y-1">
                  {detalle.pasos.map((p) => (
                    <li key={p.clave} className={`flex items-start gap-2 ${p.es_actual
                      ? 'text-indigo-700 font-medium' : ''}`}>
                      <span>{p.respondido ? '✓' : '•'}</span>
                      <span className="flex-1">{p.pregunta}</span>
                      <span className="text-gray-500">
                        {p.respondido
                          ? (Array.isArray(p.valor) ? p.valor.join(', ') : String(p.valor))
                          : (p.requerido ? 'pendiente' : 'omitido')}
                        {p.sensible && ' 🔒'}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
              {(detalle.errores || []).length > 0 && (
                <div className="border rounded-xl p-3 bg-red-50" data-testid="supervision-errores">
                  <p className="font-medium text-red-800 mb-1 flex items-center gap-1">
                    <XCircle className="h-4 w-4" /> Errores registrados ({detalle.errores.length})
                  </p>
                  <ul className="text-xs text-gray-700 space-y-2">
                    {detalle.errores.map((e) => (
                      <li key={e.id} className="border-b border-red-100 pb-2 last:border-0"
                          data-testid={`supervision-error-${e.id}`}>
                        <div>
                          <b>{e.tipo}</b> · {e.codigo}{e.paso ? ` · paso ${e.paso}` : ''} —
                          {' '}{e.detalle}
                          {e.resuelto
                            ? <Badge className="ml-1 bg-emerald-100 text-emerald-700">resuelto</Badge>
                            : <Badge className="ml-1 bg-red-100 text-red-700">sin resolver</Badge>}
                        </div>
                        {(e.historial || []).length > 0 && (
                          <ul className="text-[11px] text-gray-500 pl-3 mt-1">
                            {e.historial.map((h, i) => (
                              <li key={i}>· {h.accion}{h.motivo ? `: ${h.motivo}` : ''}
                                {h.fecha ? ` (${new Date(h.fecha).toLocaleString('es-MX')})` : ''}</li>
                            ))}
                          </ul>
                        )}
                        {!e.resuelto ? (
                          <div className="flex items-center gap-2 mt-1">
                            <input placeholder="Motivo (obligatorio)"
                                   value={(errRes[e.id] || {}).motivo || ''}
                                   onChange={(ev) => setErrRes({ ...errRes,
                                     [e.id]: { ...errRes[e.id], motivo: ev.target.value } })}
                                   className="border rounded px-2 py-1 text-xs flex-1"
                                   data-testid={`supervision-error-motivo-${e.id}`} />
                            <Button size="sm" variant="outline"
                                    onClick={() => resolverError(e.id, true)}
                                    data-testid={`supervision-error-resolver-${e.id}`}>
                              Marcar resuelto
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="ghost" className="mt-1"
                                  onClick={() => resolverError(e.id, false)}
                                  data-testid={`supervision-error-reabrir-${e.id}`}>
                            Reabrir
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="border rounded-xl p-3 bg-white" data-testid="supervision-revision">
                <p className="font-medium text-gray-700 mb-2">Revisión de la sesión</p>
                <label className="flex items-center gap-2 mb-2">
                  <input type="checkbox" checked={rev.revisada}
                         onChange={(e) => setRev({ ...rev, revisada: e.target.checked })}
                         data-testid="supervision-rev-check" />
                  Marcar como revisada
                </label>
                <label className="flex items-center gap-2 mb-2 text-sm">
                  <input type="checkbox" checked={rev.asignar}
                         onChange={(e) => setRev({ ...rev, asignar: e.target.checked })}
                         data-testid="supervision-rev-asignar" />
                  Asignar responsable al asesor de la sesión ({detalle.asesor?.nombre || '—'})
                </label>
                <textarea value={rev.nota} placeholder="Nota breve (opcional)"
                          onChange={(e) => setRev({ ...rev, nota: e.target.value })}
                          className="w-full border rounded-lg px-3 py-2 text-sm" rows={2}
                          maxLength={500} data-testid="supervision-rev-nota" />
                <Button size="sm" className="mt-2" onClick={guardarRevision} disabled={guardando}
                        data-testid="supervision-rev-guardar">
                  {guardando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Guardar revisión
                </Button>
                {detalle.revision?.revisada && (
                  <p className="text-xs text-emerald-700 mt-1">
                    Actualmente marcada como revisada{detalle.revision.nota
                      ? ` · «${detalle.revision.nota}»` : ''}
                  </p>
                )}
              </div>

              <p className="text-xs text-gray-400">
                Sólo lectura · datos sensibles enmascarados · sin llamadas externas.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default PanelSupervisionAgentes;
